// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect } from 'react';
import { ApiUser } from '../../types/accounts';
import { UserRole } from '../../types/auth';
import { authService } from '../../services/authService';
import { consoleService } from '../../services/consoleService';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';
import JsonEditor from '../JsonEditor';
import UnsavedChangesModal from '../UnsavedChangesModal';

interface ProfileTabProps {
  account: ApiUser;
  onAccountUpdate: (account: ApiUser) => void;
}

interface UpdateAccountRequest {
  username: string;
  display_name: string;
  avatar_url: string;
  location: string;
  timezone: string;
  metadata: string;
}

const ProfileTab: React.FC<ProfileTabProps> = ({ account, onAccountUpdate }) => {
  const [formData, setFormData] = useState({
    username: account.username || '',
    display_name: account.display_name || '',
    avatar_url: account.avatar_url || '',
    location: account.location || '',
    timezone: account.timezone || '',
  });
  const [metadata, setMetadata] = useState(account.metadata || '{}');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const sessionRole = authService.getSessionRole();

  // Track unsaved changes
  const {
    showUnsavedChangesModal,
    isSaving,
    confirmLeave,
    saveAndLeave,
    cancelLeave
  } = useUnsavedChanges({
    hasUnsavedChanges,
    onSave: async () => {
      await handleSubmit(new Event('submit') as any);
    },
    message: 'You have unsaved changes to the account profile. Are you sure you want to leave without saving?'
  });

  useEffect(() => {
    // Update form data when account changes
    setFormData({
      username: account.username || '',
      display_name: account.display_name || '',
      avatar_url: account.avatar_url || '',
      location: account.location || '',
      timezone: account.timezone || '',
    });
    setMetadata(account.metadata || '{}');
    setHasUnsavedChanges(false); // Reset unsaved changes when account data changes
  }, [account]);

  // Check for form field changes
  useEffect(() => {
    const originalFormData = {
      username: account.username || '',
      display_name: account.display_name || '',
      avatar_url: account.avatar_url || '',
      location: account.location || '',
      timezone: account.timezone || '',
    };

    const hasFormChanges = Object.keys(formData).some(
      key => formData[key as keyof typeof formData] !== originalFormData[key as keyof typeof originalFormData]
    );

    const hasMetadataChanges = metadata !== (account.metadata || '{}');

    setHasUnsavedChanges(hasFormChanges || hasMetadataChanges);
  }, [formData, metadata, account]);

  const updateAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleMetadataChange = (value: string) => {
    setMetadata(value);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.username.trim()) {
      errors.username = 'Username is required';
    }

    // Validate JSON metadata
    try {
      JSON.parse(metadata);
    } catch (e) {
      errors.metadata = 'Invalid JSON format';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      throw new Error('Validation failed');
    }

    setError('');
    setSuccess(false);
    setUpdating(true);

    try {
      const updateRequest: UpdateAccountRequest = {
        username: formData.username,
        display_name: formData.display_name,
        avatar_url: formData.avatar_url,
        location: formData.location,
        timezone: formData.timezone,
        metadata: metadata,
      };

      // Call the actual API to update the account
      await consoleService.updateAccount('', account.id, updateRequest);
      
      console.log('Update account request:', updateRequest);
      setSuccess(true);
      
      // Update local state with the new values
      const updatedAccount = {
        ...account,
        ...formData,
        metadata: metadata,
      };
      onAccountUpdate(updatedAccount);
      setHasUnsavedChanges(false); // Clear unsaved changes after successful save
      
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update account';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="profile-tab-clean">
      {error && (
        <div className="profile-alert profile-alert-error">
          <span className="alert-icon">⚠️</span>
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError('')}>×</button>
        </div>
      )}

      {success && (
        <div className="profile-alert profile-alert-success">
          <span className="alert-icon">✓</span>
          <span>Account updated successfully</span>
          <button className="alert-close" onClick={() => setSuccess(false)}>×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="profile-form-clean">
        {/* Basic Info Section */}
        <div className="profile-section">
          <h3 className="section-title">Basic Information</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>User ID</label>
              <div className="field-value readonly">
                <code>{account.id}</code>
              </div>
            </div>
            <div className="form-field">
              <label>Username</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                className={validationErrors.username ? 'error' : ''}
                placeholder="Enter username"
                disabled={!updateAllowed()}
              />
              {validationErrors.username && (
                <span className="field-error">{validationErrors.username}</span>
              )}
            </div>
            <div className="form-field">
              <label>Display Name</label>
              <input
                type="text"
                value={formData.display_name}
                onChange={(e) => handleInputChange('display_name', e.target.value)}
                placeholder="Enter display name"
                disabled={!updateAllowed()}
              />
            </div>
            <div className="form-field">
              <label>Avatar URL</label>
              <input
                  type="text"
                  value={formData.avatar_url}
                  onChange={(e) => handleInputChange('avatar_url', e.target.value)}
                  placeholder="Enter avatar URL"
                  disabled={!updateAllowed()}
              />
            </div>
          </div>
        </div>

        {/* Timestamps Section */}
        <div className="profile-section">
          <h3 className="section-title">Account Timeline</h3>
          <div className="form-grid">
            <div className="form-field">
              <label>Created</label>
              <div className="field-value readonly timestamp">
                <span className="timestamp-icon">📅</span>
                {formatDate(account.create_time)}
              </div>
            </div>
            <div className="form-field">
              <label>Last Updated</label>
              <div className="field-value readonly timestamp">
                <span className="timestamp-icon">🔄</span>
                {formatDate(account.update_time)}
              </div>
            </div>
          </div>
        </div>

        {/* Metadata Section */}
        <div className="profile-section">
          <h3 className="section-title">Metadata</h3>
          <div className="metadata-editor">
            <JsonEditor
              value={metadata}
              onChange={handleMetadataChange}
              placeholder="Enter JSON metadata"
              rows={10}
              disabled={!updateAllowed()}
              label=""
              error={validationErrors.metadata}
              onChangeDetected={() => {}}
            />
          </div>
        </div>

        {/* Save Button */}
        {updateAllowed() && (
          <div className="profile-actions">
            <button
              type="submit"
              className="btn-save"
              disabled={updating || !hasUnsavedChanges}
            >
              {updating ? (
                <>
                  <span className="spinner"></span>
                  Saving...
                </>
              ) : (
                <>
                  <span>💾</span>
                  Save Changes
                </>
              )}
            </button>
            {hasUnsavedChanges && (
              <span className="unsaved-indicator">You have unsaved changes</span>
            )}
          </div>
        )}
      </form>

      <UnsavedChangesModal
        isOpen={showUnsavedChangesModal}
        message="You have unsaved changes to the account profile. Are you sure you want to leave without saving?"
        onSaveAndLeave={saveAndLeave}
        onLeaveWithoutSaving={confirmLeave}
        onCancel={cancelLeave}
        isSaving={isSaving}
        showSaveOption={updateAllowed()}
      />
    </div>
  );
};

export default ProfileTab;
